/**
 * Void Artillery (bonkjs) — Phase 3: Visual Polish
 */

import { Game, Camera, Time, Input } from 'bonkjs';
import type { Renderer as PixiRendererType } from 'pixi.js';
import {
  WORLD, PHYSICS, TURN_FLOW, VOID, BURST,
  TANK_VISUAL, CEILING_BOUNCE, ASCENT, setCanvasWidth,
} from './constants';
import { generateTerrain } from './terrain/generation';
import { getGroundY, updateScorchDecay } from './terrain/grid';
import { initSmoothing, updateSmoothing } from './terrain/sand-physics';
import { updateTankVisuals } from './rendering/tanks';
import { ScreenShake } from './rendering/screen-shake';
import { MenuRenderer } from './rendering/menu';
import { SplashScreen } from './rendering/splash';
import { updateTankPhysics } from './physics/tanks';
import { updatePhase } from './game/phases';
import { applyEffects, type Renderers } from './game/effects';
import { localInput } from './input';
import { MultiplayerManager } from './net/multiplayer';
import { updateWindShift } from './game/wind';
import { updateVoidRise, erodeTerrainAtVoid } from './game/turns';
import { initViewport } from './viewport';
import { parseLevelJSON, loadLevel, setActiveLevel, getActiveLevel, mergeThemeOverrides } from './game/level-loader';
import { setCustomThemeOverride } from './stages';
import { isSoloMode, modeIsAscent } from './game/mode';
import { processVortexHoles } from './game/vortex-holes';
import { processQuakeSlamLandings } from './game/glitch-effects';
import { GLITCH_QUAKE_SLAM, GLITCH_LOW_GRAVITY, GLITCH } from './constants/glitch';
import { createGameState } from './game/create-state';
import { createRenderers } from './rendering/create-renderers';
import { getAllStageIds } from './stages';
import { setupInput, updateInputModes, updateDirectAim, getActiveInputPlayer } from './input/setup';
import { AIController } from './ai';
import type { InputProvider } from './input';
// Import tanks module to register tank definitions at startup
import './tanks';
import { TutorialController, TutorialInputFilter, TUTORIAL_STEPS, OFFENSE_STEP_COUNT } from './tutorial';
import { TutorialRenderer } from './rendering/tutorial/tutorial-renderer';
import { initAudio, startMusic, onMusicStart } from './audio';
import { Text } from 'pixi.js';
import { Z } from './constants/visual';

// Async IIFE — avoids top-level await which deadlocks PixiJS 8 in Vite production builds.
(async () => {
  // Adaptive canvas width — only expand on touch devices where extra gutter
  // space is needed for virtual joystick controls.
  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  const deviceAspect = window.innerWidth / window.innerHeight;
  const dynamicWidth = isTouch
    ? Math.round(Math.max(1920, Math.min(2400, 1080 * deviceAspect)))
    : 1920;
  setCanvasWidth(dynamicWidth);

  // Boot game
  const game = new Game();
  const { canvas, app, world: worldContainer, ui: uiContainer } = await game.init({
    width: WORLD.CANVAS_WIDTH,
    height: WORLD.CANVAS_HEIGHT,
    backgroundColor: 0x0a0a15,
    preference: 'webgl',
  });
  document.getElementById('app')!.appendChild(canvas);
  document.getElementById('loading')?.remove();
  const renderer = app.renderer as PixiRendererType;

  // Responsive viewport scaling
  initViewport(canvas);

  // Input setup — touch, gamepad, charge state machines, DOM listeners
  const input = setupInput(canvas, uiContainer);

  // Post-render screen shake
  const screenShake = new ScreenShake();

  // Generate terrain or load custom level from ?level= URL param
  const playerCount = 2;
  const urlParams = new URLSearchParams(window.location.search);
  const levelParam = urlParams.get('level');
  let spawnXs: number[];
  let spawnYs: number[] | undefined;
  let terrainResult: { seed: number; biomeDisplayName: string; featureDisplayName: string | null; stageLeft: number; stageRight: number };

  if (levelParam) {
    try {
      // Try .gbstage first, fall back to .json
      let resp = await fetch(`levels/${levelParam}.gbstage`);
      if (!resp.ok) resp = await fetch(`levels/${levelParam}.json`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const level = parseLevelJSON(json);
      const loaded = loadLevel(level);
      spawnXs = loaded.spawnXs.slice(0, playerCount);
      spawnYs = loaded.spawnYs.slice(0, playerCount);
      terrainResult = {
        seed: 0,
        biomeDisplayName: level.name,
        featureDisplayName: null,
        stageLeft: loaded.stageLeft,
        stageRight: loaded.stageRight,
      };
      // Apply custom theme overrides if present (v3 format)
      if (level.themeOverrides && level.baseThemeId) {
        const mergedTheme = mergeThemeOverrides(level.baseThemeId, level.themeOverrides);
        setCustomThemeOverride(mergedTheme);
      } else {
        setCustomThemeOverride(null);
      }
    } catch (err) {
      console.warn('Failed to load custom level, falling back to generation:', err);
      setActiveLevel(null);
      setCustomThemeOverride(null);
      const result = generateTerrain(playerCount);
      terrainResult = result;
      spawnXs = result.spawnXs;
      spawnYs = result.spawnYs;
    }
  } else {
    const result = generateTerrain(playerCount);
    terrainResult = result;
    spawnXs = result.spawnXs;
    spawnYs = result.spawnYs;
  }

  // Initialize post-crater smoothing
  initSmoothing();

  // Create game state
  const state = createGameState(
    spawnXs, getGroundY,
    terrainResult.seed, terrainResult.biomeDisplayName,
    terrainResult.featureDisplayName,
    terrainResult.stageLeft, terrainResult.stageRight,
    spawnYs,
  );

  // AI controllers — one per AI player, null for human players
  let aiControllers: (AIController | null)[] = [null, null];

  function rebuildAIControllers(): void {
    aiControllers = state.players.map((p, i) =>
      p.isAI && p.aiDifficulty > 0 ? new AIController(i, p.aiDifficulty) : null
    );
  }

  /** Get the correct InputProvider for the current frame's active player. */
  function getInputForFrame(): InputProvider {
    if (state.multiplayer?.enabled) return mp.getInputForFrame();
    const phase = state.phase;
    // Non-gameplay phases always use local input so the human player can
    // navigate menus regardless of currentPlayer. Without this, returning to
    // menu after a match that ended on the CPU's turn (currentPlayer=1) routes
    // input through aiControllers[1], which swallows all human input.
    if (phase === 'game_over' || phase === 'round_start'
      || phase === 'title_screen' || phase === 'mode_select'
      || phase === 'tank_select' || phase === 'stage_select'
      || phase === 'skins_shop' || phase === 'mp_lobby'
      || phase === 'mp_waiting') {
      return mp.getInputForFrame();
    }
    let activePlayer = state.currentPlayer;
    if (phase === 'di_window' && state.diActivePlayer >= 0) activePlayer = state.diActivePlayer;
    else if (phase === 'burst_window' && state.burstActivePlayer >= 0) activePlayer = state.burstActivePlayer;
    else if (phase === 'defensive_movement' && state.defenseActivePlayer >= 0) activePlayer = state.defenseActivePlayer;
    const ctrl = aiControllers[activePlayer];
    if (ctrl) return ctrl.input;
    // AI player with no controller (passive dummy, e.g. tutorial) — block all input
    const activeP = state.players[activePlayer];
    if (activeP?.isAI) {
      return { getKey: () => false };
    }
    const baseInput = mp.getInputForFrame(); // falls through to localInput for local human
    // Tutorial mode: filter input through tutorial key gate
    if (state.modeState.mode === 'tutorial') {
      return { getKey: (key: string) => tutorialInputFilter.getKey(key) && baseInput.getKey(key) };
    }
    return baseInput;
  }

  // Apply custom level's stage theme (if loaded from ?level= param)
  const activeCustomLevel = getActiveLevel();
  if (activeCustomLevel?.stageId) {
    state.stageId = activeCustomLevel.stageId;
  }

  // Dev tools — runtime constants tweaker (tree-shaken in production)
  if (import.meta.env.DEV) {
    const { registerTweakers } = await import('./dev/tweaker-setup');
    registerTweakers();
  }

  // Initialize all renderers
  const r = createRenderers(worldContainer, uiContainer, renderer, state.players);

  // Wire debug overlay toggles
  if (r.debugOverlay) {
    input.setDebugToggle(() => r.debugOverlay?.toggle());
  }
  if (r.netDebugOverlay) {
    input.setNetDebugToggle(() => r.netDebugOverlay?.toggle());
  }

  // Initialize camera
  const camera = new Camera(worldContainer, {
    viewport: { width: WORLD.CANVAS_WIDTH, height: WORLD.CANVAS_HEIGHT },
    zoom: WORLD.SCALE,
    followSmoothing: 5,
    bounds: {
      minX: 0,
      minY: 0,
      maxX: WORLD.VIRTUAL_WIDTH,
      maxY: WORLD.VIRTUAL_HEIGHT,
    },
  });
  camera.follow(() => {
    if (modeIsAscent(state.modeState.mode)) {
      if (state.phase === 'firing' && state.projectiles.length > 0) {
        return [state.projectiles[0].x, state.projectiles[0].y];
      }
      const p = state.players[state.currentPlayer];
      return [p.x, p.y];
    }
    // Wide stages: camera follows action horizontally
    if (WORLD.VIRTUAL_WIDTH > 2560) {
      // During firing, follow the projectile
      if (state.phase === 'firing' && state.projectiles.length > 0) {
        return [state.projectiles[0].x, WORLD.VIRTUAL_HEIGHT / 2];
      }
      // Otherwise, follow the midpoint of alive players
      const alive = state.players.filter(p => p.alive);
      if (alive.length > 0) {
        const midX = alive.reduce((sum, p) => sum + p.x, 0) / alive.length;
        return [midX, WORLD.VIRTUAL_HEIGHT / 2];
      }
    }
    return [WORLD.VIRTUAL_WIDTH / 2, WORLD.VIRTUAL_HEIGHT / 2];
  });

  // Wire camera to background for parallax + random menu theme
  r.background.setCamera(camera);
  const menuStageIds = getAllStageIds().filter(id => id !== 'random');
  const menuStageId = menuStageIds[Math.floor(Math.random() * menuStageIds.length)];
  r.background.setTheme(menuStageId);
  r.voidRenderer.setTheme(menuStageId);

  // Menu renderer needs camera
  const menuRenderer = new MenuRenderer(uiContainer, worldContainer, r.voidRenderer, r.particles, r.effects, camera, screenShake, r.hud);

  // Splash screen
  const splash = new SplashScreen(uiContainer, r.effects, screenShake);
  await document.fonts.load("140px 'Black Ops One'");
  splash.buildLetters();
  splash.start(() => {
    splash.destroy();
  });

  // Init audio on first user gesture (click or keypress)
  // Music credit text — created once, shown when music starts
  const creditText = new Text({
    text: '\u266A Drifting Beyond the Stars \u2014 Hitctrl (CC-BY 3.0)',
    style: {
      fontFamily: 'monospace',
      fontSize: 16,
      fill: 0xffffff,
      align: 'right',
    },
  });
  creditText.anchor.set(1, 1);
  creditText.x = WORLD.CANVAS_WIDTH - 12;
  creditText.y = WORLD.CANVAS_HEIGHT - 8;
  creditText.zIndex = Z.HUD_BASE;
  creditText.alpha = 0;
  uiContainer.addChild(creditText);

  let creditFadeTimer = 0;
  const CREDIT_SHOW_DURATION = 6; // seconds at full alpha
  const CREDIT_FADE_DURATION = 2; // seconds to fade down
  const CREDIT_RESTING_ALPHA = 0.15;

  onMusicStart(() => {
    creditText.alpha = 0.85;
    creditFadeTimer = CREDIT_SHOW_DURATION + CREDIT_FADE_DURATION;
  });

  const startAudio = () => {
    initAudio();
    startMusic('/music/drifting-beyond-the-stars.mp3');
    canvas.removeEventListener('pointerdown', startAudio);
    document.removeEventListener('keydown', startAudio);
  };
  canvas.addEventListener('pointerdown', startAudio);
  document.addEventListener('keydown', startAudio);

  // Tutorial system
  const tutorialController = new TutorialController();
  const tutorialInputFilter = new TutorialInputFilter(localInput, () => state);
  const tutorialRenderer = new TutorialRenderer(uiContainer);

  // Multiplayer manager
  const mp = new MultiplayerManager(state);
  mp.init();

  // Net debug log (DEV-only) — wire to multiplayer manager for desync tracking
  let netDebugLog: import('./net/debug-log').NetDebugLog | null = null;
  if (import.meta.env.DEV) {
    const { NetDebugLog } = await import('./net/debug-log');
    netDebugLog = new NetDebugLog();
    mp.setDebugLog(netDebugLog);
  }

  // Fixed-timestep gameplay (1/60s) — deterministic physics
  game.onFixedUpdate(() => {
    state.frameCount++;
    const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting' || state.phase === 'skins_shop';
    const frozen = state.frameCount < state.freezeUntil;
    if (!frozen) {
      const renderers: Renderers = {
        camera, particles: r.particles, effects: r.effects, screenShake, hud: r.hud,
        voidRenderer: r.voidRenderer, background: r.background,
        terrainRenderer: r.terrainRenderer,
        tankVisuals: r.tankVisuals,
        diRenderer: r.diRenderer, burstRenderer: r.burstRenderer,
        targetRenderer: r.targetRenderer, lotteryRenderer: r.lotteryRenderer,
        announcements: r.announcements,
        vortexHoleRenderer: r.vortexHoleRenderer,
        cardDrawRenderer: r.cardDrawRenderer,
        rogueliteHUD: r.rogueliteHUD,
      };
      if (!mp['renderers']) mp.setRenderers(renderers);
      mp.onPreUpdate();
      // Update AI controllers (skip dormant players)
      for (const ai of aiControllers) {
        if (!ai) continue;
        const p = state.players[ai.playerIndex];
        if (p?.dormant) continue;
        ai.update(state);
      }
      const fx = updatePhase(state, getInputForFrame(), mp.recoveryInput);
      // Tutorial controller — runs after phase logic, emits overlay effects
      if (state.modeState.mode === 'tutorial' && !state.paused) {
        const tutFx = tutorialController.update(state);
        fx.push(...tutFx);
      }
      // Handle tutorial pause menu actions (skip/restart via effects)
      for (const f of fx) {
        if (f.type === 'tutorial_step_advance' && state.modeState.mode === 'tutorial') {
          const maxStep = state.modeState.tutorialVariant === 'offense'
            ? OFFENSE_STEP_COUNT : TUTORIAL_STEPS.length;
          if (f.step === -1) {
            // Restart
            tutorialController.reset(state);
          } else if (f.step < maxStep) {
            // Skip to step
            state.modeState.tutorialStep = f.step;
            state.modeState.tutorialStepFrame = state.frameCount;
            state.modeState.tutorialSuccessHold = 0;
          } else {
            state.modeState.tutorialComplete = true;
          }
        }
      }
      applyEffects(fx, renderers);
      // Rebuild AI controllers on round start or survival respawn
      for (const f of fx) {
        if (f.type === 'phase_transition' && f.to === 'round_start') {
          rebuildAIControllers();
          for (const ai of aiControllers) ai?.resetForRound();
          // Initialize tutorial controller on round start
          if (state.modeState.mode === 'tutorial') {
            tutorialController.reset(state);
          }
        }
        if (f.type === 'rebuild_ai') {
          rebuildAIControllers();
          for (const ai of aiControllers) ai?.resetForRound();
        }
      }
      mp.onPostUpdate(fx);
      // Post-crater smoothing
      const disconnectFrozen = !!state.multiplayer?.disconnectPhase;
      if (!isMenu && !state.paused && !disconnectFrozen) {
        const smoothFx = updateSmoothing(state);
        if (smoothFx.length > 0) applyEffects(smoothFx, renderers);
      }
      const grappleRetractingIndex = state.grapple.active?.phase === 'retracting'
        ? state.grapple.active.playerIndex : undefined;
      const isDefenseWalking = state.phase === 'defensive_movement' && state.defenseActivePlayer >= 0
        && state.players[state.defenseActivePlayer].walkDeltaX !== 0;
      const walkingIndex = (state.phase === 'aiming' && state.players[state.currentPlayer].walkDeltaX !== 0)
        ? state.currentPlayer
        : (isDefenseWalking ? state.defenseActivePlayer : undefined);
      const defenseActive = state.phase === 'defensive_movement' ? state.defenseActivePlayer : undefined;
      const ceilingY = CEILING_BOUNCE.ENABLED
        ? (modeIsAscent(state.modeState.mode)
          ? Math.max(ASCENT.CEILING_BLAST_ZONE, state.voidY - ASCENT.BAND_HEIGHT)
          : CEILING_BOUNCE.STANDARD_CEILING_Y)
        : undefined;
      const tutFrozenIndices = state.modeState.tutorialEdgeguardFreeze
        ? state.edgeguardKnockedIndices : undefined;
      if (state.phase !== 'di_window' && state.phase !== 'burst_window' && !isMenu && !state.paused && !disconnectFrozen) {
        // Snapshot airborne state for QUAKE_SLAM landing detection
        const quakeSlamActive = state.activeGlitch === GLITCH_QUAKE_SLAM;
        const preGrounded = quakeSlamActive
          ? state.players.map(p => p.grounded || p.settled)
          : undefined;

        // LOW_GRAVITY: reduce gravity for tanks
        const gravMult = state.activeGlitch === GLITCH_LOW_GRAVITY ? GLITCH.LOW_GRAVITY_MULT : undefined;

        updateTankPhysics(state.players, r.particles, screenShake, grappleRetractingIndex, walkingIndex, r.bumperRenderer, defenseActive, ceilingY, tutFrozenIndices, gravMult);

        // QUAKE_SLAM: create craters where tanks just landed
        if (quakeSlamActive && preGrounded) {
          const quakeFx: import('./game/effects').GameEffect[] = [];
          processQuakeSlamLandings(state, preGrounded, quakeFx);
          if (quakeFx.length > 0) applyEffects(quakeFx, renderers);
        }
      }
      // Vortex portals — tentacle lash, portal walk-through, beam sequence (after tank physics)
      if (state.phase !== 'di_window' && state.phase !== 'burst_window' && !isMenu && !state.paused && !disconnectFrozen && (state.vortexHoles.length > 0 || state.beamActive)) {
        const vortexFx: import('./game/effects').GameEffect[] = [];
        processVortexHoles(state, vortexFx);
        if (vortexFx.length > 0) applyEffects(vortexFx, renderers);
      }
      // Control-restored effects
      for (let i = 0; i < state.players.length; i++) {
        const p = state.players[i];
        if (p.controlRestored) {
          p.controlRestored = false;
          applyEffects([
            { type: 'tank_flash', playerIndex: i,
              bright: BURST.CONTROL_FLASH_BRIGHT, dark: BURST.CONTROL_FLASH_DARK,
              duration: BURST.CONTROL_FLASH_DURATION },
            { type: 'screen_flash', color: BURST.GOLD, alpha: BURST.CONTROL_SCREEN_FLASH_ALPHA },
          ], renderers);
        }
      }
      // Void rise (paused during roguelite card draw)
      if (!isMenu && !state.paused && !disconnectFrozen && !isSoloMode(state.modeState.mode) && state.phase !== 'defensive_movement' && state.phase !== 'roguelite_card_draw') {
        updateVoidRise(state);
        const erosionFx = erodeTerrainAtVoid(state);
        if (erosionFx.length > 0) applyEffects(erosionFx, renderers);
      }
    }
  });

  // Variable-timestep visuals — smooth at any refresh rate
  game.onUpdate(() => {
    // Two-gamepad polling: during gameplay with two pads, only poll the active player's gamepad
    const twoGp = input.gamepads[0].connected && input.gamepads[1].connected;
    state.twoGamepadsConnected = twoGp;
    const isMenuForPoll = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting' || state.phase === 'skins_shop';
    if (!twoGp || isMenuForPoll || state.paused || state.phase === 'game_over') {
      // Single gamepad, menu, paused, or game over — poll both
      input.gamepads[0].show();
      input.gamepads[0].update();
      input.gamepads[1].show();
      input.gamepads[1].update();
    } else {
      // Gameplay with two gamepads — only active player's pad polls
      const activeGp = getActiveInputPlayer(state);
      const inactiveGp = activeGp === 0 ? 1 : 0;
      input.gamepads[inactiveGp].hide(); // releases held keys
      input.gamepads[activeGp].show();
      input.gamepads[activeGp].update();
    }
    updateInputModes(input, state, canvas);

    const dt = Time.deltaTime;
    const frozen = state.frameCount < state.freezeUntil;
    const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting' || state.phase === 'skins_shop';

    // Direct aim (gamepad right stick, mouse, touch aim stick)
    updateDirectAim(input, state, canvas, camera);

    // Splash always updates
    splash.update(dt);

    // Music credit fade
    if (creditFadeTimer > 0) {
      creditFadeTimer -= dt;
      if (creditFadeTimer <= 0) {
        creditText.alpha = CREDIT_RESTING_ALPHA;
      } else if (creditFadeTimer < CREDIT_FADE_DURATION) {
        const t = creditFadeTimer / CREDIT_FADE_DURATION;
        creditText.alpha = CREDIT_RESTING_ALPHA + (0.85 - CREDIT_RESTING_ALPHA) * t;
      }
    }

    menuRenderer.update(state, dt);
    r.hud.update(state, dt);

    // Hide game-world visuals during menu (background stays visible)
    r.terrainRenderer.setVisible(!isMenu);
    r.bumperRenderer.setVisible(!isMenu);
    r.eyeRenderer.setVisible(!isMenu);
    for (const tv of r.tankVisuals) {
      tv.outerContainer.visible = !isMenu;
      tv.aimLine.visible = !isMenu;
      tv.trajectoryGfx.visible = !isMenu;
    }

    // Game visual updates only during gameplay
    if (!isMenu) {
      updateTankVisuals(state.players, r.tankVisuals, state.currentPlayer, state.phase === 'aiming', dt, state.wind, r.particles, state.modeState.tutorialOpponentFade);
      r.eyeRenderer.update(state, dt);
      r.windRenderer.update(dt, state.wind);
      r.voidRenderer.setPlayerPositions(state.players.map(p => ({ x: p.x, y: p.y, alive: p.alive })));
      // Grapple rope visual
      const grapplePlayer = state.grapple.active
        ? state.players[state.grapple.active.playerIndex]
        : state.players[state.currentPlayer];
      r.ropeRenderer.update(
        state.grapple.active,
        grapplePlayer.x, grapplePlayer.y, grapplePlayer.color, dt,
      );
      updateScorchDecay(dt);
      r.terrainRenderer.updateHotEdges(dt);
      updateWindShift(state, dt);
      r.diRenderer.update(state, dt);
      r.burstRenderer.update(state, dt);
      r.lotteryRenderer.update(state, dt);
      r.announcements.update(dt);
      // Tutorial overlay
      if (state.modeState.mode === 'tutorial') {
        tutorialRenderer.show();
        tutorialRenderer.update(state, dt);
      }
      r.opponentArrow.update(state, camera, dt);
      // Railgun beam + edgeguard charge bar
      const edgeguardCharge = state.phase === 'edgeguard' ? {
        chargeT: state.edgeguardChargeTarget > 0 ? state.edgeguardChargeFrame / state.edgeguardChargeTarget : 1,
        charged: state.edgeguardChargeTarget > 0 ? state.edgeguardChargeFrame >= state.edgeguardChargeTarget : true,
        fired: state.edgeguardFired,
        attackerX: state.players[state.currentPlayer].x,
        attackerY: state.players[state.currentPlayer].y,
        attackerAngle: state.players[state.currentPlayer].angle,
        turretLength: TANK_VISUAL.TURRET_LENGTH,
        active: true,
      } : undefined;
      // Safety net: clear stale beam if we're no longer in edgeguard phase
      if (state.edgeguardBeam && state.phase !== 'edgeguard') {
        state.edgeguardBeam = null;
      }
      r.railgunBeamRenderer.update(state.edgeguardBeam, edgeguardCharge);
      r.bumperRenderer.updateProximity(state.players);
      r.bumperRenderer.update(dt);
      // Roguelite HUD + card draw
      r.cardDrawRenderer.update(state, dt);
      r.rogueliteHUD.update(state);
      // Target Blast targets + exit gate
      if (state.modeState.mode === 'target_blast') {
        r.targetRenderer.update(state.modeState.targets, dt);
        r.targetRenderer.setVisible(true);
        if (state.modeState.exitGateX !== 0 || state.modeState.exitGateY !== 0) {
          r.targetRenderer.setExitGate(state.modeState.exitGateX, state.modeState.exitGateY);
          r.targetRenderer.setExitGateActive(state.modeState.exitGateActive);
        }
      } else {
        r.targetRenderer.setVisible(false);
      }
    }

    // Vortex hole visuals — runs outside !isMenu gate so portals are cleared
    // when returning to menu (gfx.clear() must always fire to prevent stale visuals)
    r.vortexHoleRenderer.update(state, dt);

    // Tutorial overlay: hide when on menu screens (runs outside !isMenu gate)
    if (isMenu || state.modeState.mode !== 'tutorial') {
      tutorialRenderer.hide();
    }

    r.debugOverlay?.update(state);
    if (netDebugLog) {
      r.netDebugOverlay?.update(state, netDebugLog);
      r.netDebugOverlay?.updateCompactIndicator(state, netDebugLog);
    }
    r.effects.update(dt, frozen);
    r.background.update(dt, state.wind);
    r.voidRenderer.update(dt, state.voidY);
    r.particles.update(dt, frozen);
  });

  game.onLateUpdate(() => {
    if (camera.bounds) {
      if (modeIsAscent(state.modeState.mode)) {
        camera.bounds.minY = Math.max(ASCENT.CEILING_BLAST_ZONE, state.voidY - ASCENT.BAND_HEIGHT);
      } else {
        camera.bounds.minY = 0;
      }
      camera.bounds.maxX = WORLD.VIRTUAL_WIDTH;
      camera.bounds.maxY = WORLD.VIRTUAL_HEIGHT;
    }
    camera.update();
    screenShake.update();
  });

  game.start();

})();
